type FieldOf<T, K extends keyof T> = { readonly [P in K]-?: ValueOf<T, K> };
type ValueOf<T, K extends keyof T> = NonNullable<T[K]>;

export class OpenFormMap<R extends string, T extends object> extends Map<R, T> {
  constructor(map?: Map<R, T> | readonly (readonly [R, T])[] | Iterable<readonly [R, T]> | null) {
    super(map);
  }

  assign<K extends keyof T>(id: R, key: K, patch: T[K]): this {
    return super.set(id, Object.assign(super.get(id) ?? ({} as T), { [key]: patch }));
  }

  entriesOf<K extends keyof T>(key: K, result: [R, ValueOf<T, K>][] = []): [R, ValueOf<T, K>][] {
    for (const [id, value] of super.entries()) {
      if (this.#is(key, value)) {
        result.push([id, value[key]]);
      }
    }
    return result;
  }

  findOf<K extends keyof T>(key: K, cb: (value: T & FieldOf<T, K>, id: R) => unknown): [R, ValueOf<T, K>] | [] {
    for (const [id, value] of super.entries()) {
      if (this.#is(key, value) && cb(value, id)) {
        return [id, value[key]];
      }
    }
    return [];
  }

  forEachOf<K extends keyof T>(key: K, cb: (value: T & FieldOf<T, K>, id: R) => unknown): void {
    for (const [id, value] of super.entries()) {
      if (this.#is(key, value)) {
        cb(value, id);
      }
    }
  }

  hasOf<K extends keyof T>(key: K, cb: (value: T & FieldOf<T, K>, id: R) => unknown): boolean {
    for (const [id, value] of super.entries()) {
      if (this.#is(key, value) && cb(value, id)) {
        return true;
      }
    }
    return false;
  }

  valuesOf<K extends keyof T>(key: K, result: ValueOf<T, K>[] = []): ValueOf<T, K>[] {
    for (const value of super.values()) {
      if (this.#is(key, value)) {
        result.push(value[key]);
      }
    }
    return result;
  }

  #is<K extends keyof T>(key: K, value: T): value is T & FieldOf<T, K> {
    return value?.[key] !== undefined && value[key] !== null;
  }
}
